import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { Fragment } from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Subject } from 'rxjs';

import type { MentionNode } from './nodes';
import type {
  MentionElement,
  MentionElementGroup,
  MentionListController,
  MentionsListOpenOptions,
  MentionsPluginEvents,
} from './types';
import { MentionsPluginPickerBeforeOpen, MentionsPluginPickerClose } from './types';
import type { OverlayPosition, RtSchemaType } from '../../editor/types';

export class MentionServiceInternal {
  public data: MentionElement[] | MentionElementGroup[];
  public filterData: (query: string) => MentionElement[] | MentionElementGroup[];

  public currentOrigin: number | HTMLElement;
  protected currentQuery: string;
  protected preferredPickerPositions = [
    <const>{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    <const>{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  constructor(
    protected editorView: EditorView,
    protected eventStream$: Subject<MentionsPluginEvents>,
    protected mentionListController?: MentionListController,
  ) {}

  public mentionsMatchHandler(
    state: EditorState,
    match: RegExpMatchArray | null,
    start: number,
    end: number,
  ) {
    const mentionsOpen = this.mentionListController!.isOpen();
    if (!match) return this.closeMentionsList();
    const matchedText = match[0];

    const query = (this.currentQuery = matchedText.replace('@', ''));
    const filteredData = this.filterData(query);
    if (!filteredData.length) return this.closeMentionsList();

    if (
      filteredData.some(possibleGrouped => 'data' in possibleGrouped) &&
      (<MentionElementGroup[]>filteredData).every(group => !group.data.length)
    ) {
      return this.closeMentionsList();
    }

    const from = matchedText[0].match(/\s/) ? start + 1 : start;
    this.currentOrigin = from;

    if (!mentionsOpen) {
      const position = this.editorView.coordsAtPos(from);
      this.openMentionsList(position, {
        preferredPositions: this.preferredPickerPositions,
      });
    }

    this.updateMentionListData(from, end, filteredData);
  }

  public updateMentionListData(
    from: number,
    to: number,
    data: MentionElement[] | MentionElementGroup[],
  ) {
    this.mentionListController!.setSelectCallback(this.getSelectMentionCallback(from, to));
    this.mentionListController!.setData(data);
  }

  public openMentionsList(position: OverlayPosition | DOMRect, options?: MentionsListOpenOptions) {
    this.eventStream$.next(new MentionsPluginPickerBeforeOpen());
    const { backdropClick$ } = this.mentionListController!.open(position, options);
    backdropClick$.subscribe(() => {
      this.closeMentionsList();
      this.editorView.focus();
    });
  }

  public createMentionNode(
    schema: RtSchemaType<typeof MentionNode>,
    mentionItem: MentionElement,
  ): ProsemirrorNode {
    return schema.nodes.mention.createChecked(
      {
        'data-id': mentionItem.id,
        'data-type': mentionItem.type,
      },
      schema.text(mentionItem.name),
    );
  }

  public closeMentionsList(): void {
    this.mentionListController!.close();
    this.eventStream$.next(new MentionsPluginPickerClose());
  }

  protected getPositionFromCurrentOrigin() {
    if (this.currentOrigin instanceof HTMLElement) {
      return this.currentOrigin.getBoundingClientRect();
    } else {
      return this.editorView.coordsAtPos(this.currentOrigin);
    }
  }

  private getSelectMentionCallback(start: number, end: number) {
    return (selectedEl: MentionElement) => {
      const {
        state: { schema, tr },
      } = this.editorView;
      const mentionNode = this.createMentionNode(schema, selectedEl);
      const fragment = Fragment.from([mentionNode, schema.text(' ')]);
      tr.replaceWith(start, end, fragment);
      this.editorView.dispatch(tr);

      this.closeMentionsList();
      this.editorView.focus();
    };
  }
}
